/**
 * «Incluida con membresía»: qué membresías y paquetes de clases incluyen este
 * servicio, y lo que da cada uno (fase 7 de las cuentas de clientes).
 *
 * Una membresía (precio recurrente) da reservas por periodo, o ilimitadas. Un
 * paquete (precio único) da N clases que valen tantos días. La regla es de la
 * membresía, no del servicio: vale para todos los servicios que la incluyan, y
 * se dice. Desde el plan Pro.
 */
import { useEffect, useState } from 'react'
import { IconLock } from '@tabler/icons-react'
import { getBookingInclusions, type InclusionCandidate } from '../../../lib/api'
import { useI18n } from '../../../lib/i18n'

export type InclusionDraft = InclusionCandidate & { selected: boolean }

/** Lo elegido es válido: al menos una, y cada paquete con sus clases. */
export function inclusionsValid(drafts: InclusionDraft[]): boolean {
  const chosen = drafts.filter((draft) => draft.selected)
  return chosen.length > 0 && chosen.every((draft) => draft.kind === 'membership' || (draft.packCredits ?? 0) > 0)
}

export function MembershipInclusionsPicker({ projectId, offerId, token, value, onChange, onOpenPlans }: {
  projectId: string
  /** Nulo mientras el servicio se está creando. */
  offerId: string | null
  token: string
  value: InclusionDraft[] | null
  onChange: (next: InclusionDraft[]) => void
  onOpenPlans?: (() => void) | undefined
}) {
  const { t } = useI18n()
  const [state, setState] = useState<{ ready: boolean; allowed: boolean } | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let alive = true
    getBookingInclusions(projectId, offerId ?? 'new', token)
      .then((result) => {
        if (!alive) return
        setState({ ready: result.ready, allowed: result.allowed })
        if (value === null) {
          onChange(result.candidates.map((candidate) => ({ ...candidate, selected: result.chosen.includes(candidate.offerId) })))
        }
      })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
    // Sólo al abrir: lo que se va eligiendo vive en `value`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, offerId, token])

  if (failed) return <p className="dash-svc-schedule-note">{t('svcw.membership.loadFailed')}</p>
  if (!state || value === null) return <p className="dash-svc-schedule-note">{t('common.loading')}</p>
  if (!state.allowed) {
    return (
      <div className="dash-svc-membership-lock">
        <IconLock size={16} aria-hidden="true" />
        <div>
          <strong>{t('svcw.membership.proTitle')}</strong>
          <small>{t('svcw.membership.proBody')}</small>
        </div>
        {onOpenPlans ? <button type="button" className="dash-secondary-btn" onClick={onOpenPlans}>{t('svcw.membership.seePlans')}</button> : null}
      </div>
    )
  }
  if (!state.ready) return <p className="dash-svc-schedule-note">{t('svcw.membership.notReady')}</p>
  if (!value.length) return <p className="dash-svc-schedule-note">{t('svcw.membership.empty')} {t('svcw.membership.manageHint')}</p>

  const patch = (offerId: string, change: Partial<InclusionDraft>) =>
    onChange(value.map((draft) => (draft.offerId === offerId ? { ...draft, ...change } : draft)))
  const number = (raw: string) => {
    const parsed = Number.parseInt(raw.replace(/\D/g, ''), 10)
    return Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 1000) : null
  }

  return (
    <div className="dash-svc-membership">
      <p className="dash-svc-membership-lead">{t('svcw.membership.lead')}</p>
      <ul>
        {value.map((draft) => (
          <li key={draft.offerId} className={draft.selected ? 'is-on' : ''}>
            <label className="dash-svc-membership-head">
              <input type="checkbox" checked={draft.selected} onChange={(event) => patch(draft.offerId, { selected: event.target.checked })} />
              <strong>{draft.title}</strong>
              <span className={`dash-svc-membership-kind is-${draft.kind}`}>{t(draft.kind === 'membership' ? 'svcw.membership.kindMembership' : 'svcw.membership.kindPack')}</span>
            </label>
            {draft.selected ? (
              draft.kind === 'membership' ? (
                <div className="dash-svc-membership-rule">
                  <span>{t('svcw.membership.perPeriod')}</span>
                  <select
                    value={draft.bookingsPerPeriod === null ? 'unlimited' : 'limited'}
                    onChange={(event) => patch(draft.offerId, { bookingsPerPeriod: event.target.value === 'unlimited' ? null : (draft.bookingsPerPeriod ?? 8) })}
                  >
                    <option value="unlimited">{t('svcw.membership.unlimited')}</option>
                    <option value="limited">{t('svcw.membership.limited')}</option>
                  </select>
                  {draft.bookingsPerPeriod !== null ? (
                    <input inputMode="numeric" aria-label={t('svcw.membership.perPeriod')} value={draft.bookingsPerPeriod} onChange={(event) => patch(draft.offerId, { bookingsPerPeriod: number(event.target.value) ?? 1 })} />
                  ) : null}
                </div>
              ) : (
                <div className="dash-svc-membership-rule">
                  <span>{t('svcw.membership.credits')}</span>
                  <input inputMode="numeric" aria-label={t('svcw.membership.credits')} placeholder="10" value={draft.packCredits ?? ''} onChange={(event) => patch(draft.offerId, { packCredits: number(event.target.value) })} />
                  <span>{t('svcw.membership.validFor')}</span>
                  <select
                    value={draft.packValidDays === null ? 'forever' : 'days'}
                    onChange={(event) => patch(draft.offerId, { packValidDays: event.target.value === 'forever' ? null : (draft.packValidDays ?? 90) })}
                  >
                    <option value="forever">{t('svcw.membership.noExpiry')}</option>
                    <option value="days">{t('svcw.membership.days')}</option>
                  </select>
                  {draft.packValidDays !== null ? (
                    <input inputMode="numeric" aria-label={t('svcw.membership.days')} value={draft.packValidDays} onChange={(event) => patch(draft.offerId, { packValidDays: number(event.target.value) ?? 1 })} />
                  ) : null}
                </div>
              )
            ) : null}
          </li>
        ))}
      </ul>
      <small className="dash-svc-membership-note">{t('svcw.membership.shared')} {t('svcw.membership.manageHint')}</small>
    </div>
  )
}
